import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    getUserProposals,
    getProposalCounts,
    deleteProposal,
    markAsReadyForReview,
    submitMockProposal,
    backgroundSyncProposals,
    getProposalById
} from '../services/proposalService';
import socketService from '../services/socketService';
import { usePermissions } from '../hooks/usePermissions';
import { RefreshCw, Briefcase, Search, ChevronLeft, ChevronRight, X } from 'lucide-react';
import ProposalTabs from './ProposalTabs';
import CollapsibleSection from './CollapsibleSection';
import ProposalCard from './ProposalCard';
import PromoBanner from './PromoBanner';
import { InlineProposalDetails } from './InlineProposalDetails';
import { ModalNavigation } from './ModalNavigation';

const ITEMS_PER_PAGE = 20;

export function ProposalsList() {
    const navigate = useNavigate();
    const { canAccessAdminPanel } = usePermissions();
    const [activeProposals, setActiveProposals] = useState([]);
    const [archivedProposals, setArchivedProposals] = useState([]);
    const [archivedLoaded, setArchivedLoaded] = useState(false);
    const [archivedLoading, setArchivedLoading] = useState(false);
    const [counts, setCounts] = useState({ active: 0, archived: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('active');
    const [syncing, setSyncing] = useState(false);
    const [sectionPages, setSectionPages] = useState({
        offers: 1,
        invitations: 1,
        activeProposals: 1,
        submittedProposals: 1,
        referrals: 1,
        archived: 1
    });

    // Modal navigation state
    const [selectedProposal, setSelectedProposal] = useState(null);
    const [selectedProposalIndex, setSelectedProposalIndex] = useState(-1);
    const [displayProposals, setDisplayProposals] = useState([]);

    const syncTimeoutRef = useRef(null);

    // Silently refresh active proposals + counts from DB (no loading spinner)
    const refreshActiveData = useCallback(async () => {
        try {
            const [proposalsRes, countsRes] = await Promise.all([
                getUserProposals({ statusGroup: 'active', page: 1, limit: 500 }),
                getProposalCounts()
            ]);
            if (proposalsRes.success) {
                setActiveProposals(proposalsRes.data);
            }
            if (countsRes.success) {
                setCounts(countsRes.counts);
            }
            // Invalidate archived cache so next tab click re-fetches
            setArchivedLoaded(false);
        } catch {
            // Silent — background refresh, don't show errors
        }
    }, []);

    // Load active proposals + counts on mount
    useEffect(() => {
        loadActiveProposals();
        backgroundSyncProposals();
    }, []);

    // Listen for real-time sync events via Socket.IO
    useEffect(() => {
        const handleProposalsUpdated = () => {
            refreshActiveData();
            // Clear syncing state if user triggered a manual sync
            if (syncTimeoutRef.current) {
                clearTimeout(syncTimeoutRef.current);
                syncTimeoutRef.current = null;
            }
            setSyncing(false);
        };

        socketService.on('data:proposals_updated', handleProposalsUpdated);
        return () => {
            socketService.off('data:proposals_updated', handleProposalsUpdated);
            if (syncTimeoutRef.current) {
                clearTimeout(syncTimeoutRef.current);
            }
        };
    }, [refreshActiveData]);

    const loadActiveProposals = async () => {
        try {
            setLoading(true);
            const [proposalsRes, countsRes] = await Promise.all([
                getUserProposals({ statusGroup: 'active', page: 1, limit: 500 }),
                getProposalCounts()
            ]);

            if (proposalsRes.success) {
                setActiveProposals(proposalsRes.data);
                setSectionPages({
                    offers: 1,
                    invitations: 1,
                    activeProposals: 1,
                    submittedProposals: 1,
                    referrals: 1,
                    archived: 1
                });
            } else {
                setError(proposalsRes.error || 'Failed to load proposals');
            }

            if (countsRes.success) {
                setCounts(countsRes.counts);
            }
        } catch (error) {
            setError(error.message || 'Failed to load proposals');
        } finally {
            setLoading(false);
        }
    };

    const loadArchivedProposals = async () => {
        if (archivedLoaded || archivedLoading) return;
        try {
            setArchivedLoading(true);
            const response = await getUserProposals({
                statusGroup: 'archived',
                page: 1,
                limit: 500
            });
            if (response.success) {
                setArchivedProposals(response.data);
                setArchivedLoaded(true);
                setSectionPages(prev => ({ ...prev, archived: 1 }));
            }
        } catch (error) {
            setError(error.message || 'Failed to load archived proposals');
        } finally {
            setArchivedLoading(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'archived' && !archivedLoaded) {
            loadArchivedProposals();
        }
    };

    const handleSyncUpwork = async () => {
        setSyncing(true);
        setError('');

        // Fire-and-forget — Socket.IO event will refresh data when sync completes
        backgroundSyncProposals();

        // Timeout: clear spinner after 30s if no Socket.IO event arrives (e.g., no new data found)
        syncTimeoutRef.current = setTimeout(() => {
            syncTimeoutRef.current = null;
            setSyncing(false);
            refreshActiveData(); // Final check in case Socket.IO event was missed
        }, 30000);
    };

    const handleArchive = async (proposal) => {
        if (!window.confirm('Are you sure you want to archive this proposal?')) {
            return;
        }
        // TODO: Implement archive functionality
    };

    // Handle proposal click - opens modal with navigation
    const handleProposalClick = async (proposal, index, allProposals) => {
        setSelectedProposal(proposal);
        setSelectedProposalIndex(index);
        setDisplayProposals(allProposals);
    };

    // Navigate to next/previous proposal in modal
    const handleNavigateProposal = async (direction) => {
        const newIndex = selectedProposalIndex + direction;
        if (newIndex >= 0 && newIndex < displayProposals.length) {
            const newProposal = displayProposals[newIndex];
            setSelectedProposalIndex(newIndex);
            setSelectedProposal(newProposal);
        }
    };

    // Close modal
    const handleCloseModal = () => {
        setSelectedProposal(null);
        setSelectedProposalIndex(-1);
        setDisplayProposals([]);
        // Refresh proposals to get any updates
        refreshActiveData();
    };

    // Keyboard navigation for modal
    useEffect(() => {
        if (!selectedProposal) return;

        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft') {
                handleNavigateProposal(-1);
            } else if (e.key === 'ArrowRight') {
                handleNavigateProposal(1);
            } else if (e.key === 'Escape') {
                handleCloseModal();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedProposal, selectedProposalIndex, displayProposals.length]);

    // Pagination helpers
    const paginateItems = (items, page) => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        return items.slice(start, start + ITEMS_PER_PAGE);
    };

    const getTotalPages = (items) => Math.ceil(items.length / ITEMS_PER_PAGE);

    const setSectionPage = (section, page) => {
        setSectionPages(prev => ({ ...prev, [section]: page }));
    };

    // Per-section pagination controls
    const SectionPagination = ({ section, items }) => {
        const totalPages = getTotalPages(items);
        if (totalPages <= 1) return null;
        const currentPage = sectionPages[section];

        return (
            <div className="flex items-center justify-between pt-3 mt-1 border-t border-gray-100 px-4">
                <span className="text-xs text-gray-500">
                    {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, items.length)} of {items.length}
                </span>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setSectionPage(section, currentPage - 1)}
                        disabled={currentPage === 1}
                        className="w-7 h-7 flex items-center justify-center text-sm rounded border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                        <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                            if (page === 1 || page === totalPages) return true;
                            if (totalPages <= 5) return true;
                            return Math.abs(page - currentPage) <= 1;
                        })
                        .map((page, idx, arr) => (
                            <React.Fragment key={page}>
                                {idx > 0 && arr[idx - 1] !== page - 1 && (
                                    <span className="w-7 h-7 flex items-center justify-center text-xs text-gray-400">...</span>
                                )}
                                <button
                                    onClick={() => setSectionPage(section, page)}
                                    className={`w-7 h-7 text-xs font-medium rounded transition-colors ${
                                        currentPage === page
                                            ? 'bg-primary-600 text-white'
                                            : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    {page}
                                </button>
                            </React.Fragment>
                        ))
                    }
                    <button
                        onClick={() => setSectionPage(section, currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="w-7 h-7 flex items-center justify-center text-sm rounded border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                        <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
        );
    };

    // Group active proposals into sub-sections (backend already excluded archived)
    const groupedActive = useMemo(() => {
        const groups = {
            offers: [],
            invitations: [],
            activeProposals: [],
            submittedProposals: []
        };

        activeProposals.forEach(proposal => {
            const upworkStatus = proposal.upworkStatus;
            const status = proposal.status;

            if (upworkStatus === 'Offered') {
                groups.offers.push(proposal);
            } else if (upworkStatus === 'Hired') {
                groups.invitations.push(proposal);
            } else if (status === 'submitted' || upworkStatus === 'Accepted') {
                groups.submittedProposals.push(proposal);
            } else {
                groups.activeProposals.push(proposal);
            }
        });

        return groups;
    }, [activeProposals]);

    // Tab counts from dedicated API endpoint
    const tabCounts = useMemo(() => ({
        active: counts.active,
        referrals: 0,
        archived: counts.archived
    }), [counts]);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-center h-64">
                        <div className="flex items-center gap-3 text-gray-500">
                            <RefreshCw className="w-5 h-5 animate-spin" />
                            Loading proposals...
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Promotional Banner */}
                <div className="mb-6">
                    <PromoBanner
                        id="proposals-promo"
                        message="Keep your profile up to date to increase your chances of getting hired."
                        ctaText="Edit Profile"
                        onCtaClick={() => navigate('/profile')}
                        variant="info"
                    />
                </div>

                {/* Page Header */}
                <div className="mb-6 flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-gray-900">My Proposals</h1>
                    <button
                        onClick={handleSyncUpwork}
                        disabled={syncing}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                        {syncing ? 'Syncing...' : 'Refresh'}
                    </button>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                        {error}
                    </div>
                )}

                {/* Tabs */}
                <div className="mb-6">
                    <ProposalTabs
                        activeTab={activeTab}
                        onTabChange={handleTabChange}
                        counts={tabCounts}
                    />
                </div>

                {/* Tab Content */}
                <div className="space-y-4">
                    {activeTab === 'active' && (
                        <>
                            {/* Offers Section */}
                            {groupedActive.offers.length > 0 && (
                                <CollapsibleSection
                                    title="Offers"
                                    count={groupedActive.offers.length}
                                    infoTooltip="Job offers from clients who want to hire you"
                                    defaultOpen={true}
                                >
                                    <div>
                                        {paginateItems(groupedActive.offers, sectionPages.offers).map((proposal, idx) => (
                                            <ProposalCard
                                                key={proposal._id}
                                                proposal={proposal}
                                                onArchive={handleArchive}
                                                onView={handleProposalClick}
                                                index={idx + (sectionPages.offers - 1) * ITEMS_PER_PAGE}
                                                allProposals={groupedActive.offers}
                                            />
                                        ))}
                                    </div>
                                    <SectionPagination section="offers" items={groupedActive.offers} />
                                </CollapsibleSection>
                            )}

                            {/* Invitations to Interview Section */}
                            {groupedActive.invitations.length > 0 && (
                                <CollapsibleSection
                                    title="Invitations to Interview"
                                    count={groupedActive.invitations.length}
                                    defaultOpen={true}
                                >
                                    <div>
                                        {paginateItems(groupedActive.invitations, sectionPages.invitations).map((proposal, idx) => (
                                            <ProposalCard
                                                key={proposal._id}
                                                proposal={proposal}
                                                onArchive={handleArchive}
                                                onView={handleProposalClick}
                                                index={idx + (sectionPages.invitations - 1) * ITEMS_PER_PAGE}
                                                allProposals={groupedActive.invitations}
                                            />
                                        ))}
                                    </div>
                                    <SectionPagination section="invitations" items={groupedActive.invitations} />
                                </CollapsibleSection>
                            )}

                            {/* Active Proposals Section */}
                            <CollapsibleSection
                                title="Active Proposals"
                                count={groupedActive.activeProposals.length}
                                defaultOpen={true}
                            >
                                {groupedActive.activeProposals.length > 0 ? (
                                    <>
                                        <div>
                                            {paginateItems(groupedActive.activeProposals, sectionPages.activeProposals).map(proposal => (
                                                <ProposalCard
                                                    key={proposal._id}
                                                    proposal={proposal}
                                                    onArchive={handleArchive}
                                                />
                                            ))}
                                        </div>
                                        <SectionPagination section="activeProposals" items={groupedActive.activeProposals} />
                                    </>
                                ) : (
                                    <div className="text-center py-8">
                                        <Briefcase className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                                        <p className="text-gray-500 mb-4">No active proposals yet</p>
                                        <Link
                                            to="/jobs"
                                            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
                                        >
                                            <Search className="w-4 h-4" />
                                            Search for jobs
                                        </Link>
                                    </div>
                                )}
                            </CollapsibleSection>

                            {/* Submitted Proposals Section */}
                            {groupedActive.submittedProposals.length > 0 && (
                                <CollapsibleSection
                                    title="Submitted Proposals"
                                    count={groupedActive.submittedProposals.length}
                                    defaultOpen={true}
                                >
                                    <div>
                                        {paginateItems(groupedActive.submittedProposals, sectionPages.submittedProposals).map((proposal, idx) => (
                                            <ProposalCard
                                                key={proposal._id}
                                                proposal={proposal}
                                                onArchive={handleArchive}
                                                onView={handleProposalClick}
                                                index={idx + (sectionPages.submittedProposals - 1) * ITEMS_PER_PAGE}
                                                allProposals={groupedActive.submittedProposals}
                                            />
                                        ))}
                                    </div>
                                    <SectionPagination section="submittedProposals" items={groupedActive.submittedProposals} />
                                </CollapsibleSection>
                            )}
                        </>
                    )}

                    {activeTab === 'referrals' && (
                        <CollapsibleSection
                            title="Referral Opportunities"
                            count={0}
                            defaultOpen={true}
                        >
                            <div className="text-center py-8">
                                <Briefcase className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                                <p className="text-gray-500">No referral opportunities yet</p>
                                <p className="text-sm text-gray-400 mt-2">
                                    Referral opportunities will appear here when available
                                </p>
                            </div>
                        </CollapsibleSection>
                    )}

                    {activeTab === 'archived' && (
                        <CollapsibleSection
                            title="Archived Proposals"
                            count={counts.archived}
                            defaultOpen={true}
                        >
                            {archivedLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="flex items-center gap-3 text-gray-500">
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Loading archived proposals...
                                    </div>
                                </div>
                            ) : archivedProposals.length > 0 ? (
                                <>
                                    <div>
                                        {paginateItems(archivedProposals, sectionPages.archived).map((proposal, idx) => (
                                            <ProposalCard
                                                key={proposal._id}
                                                proposal={proposal}
                                                showActions={false}
                                                onView={handleProposalClick}
                                                index={idx + (sectionPages.archived - 1) * ITEMS_PER_PAGE}
                                                allProposals={archivedProposals}
                                            />
                                        ))}
                                    </div>
                                    <SectionPagination section="archived" items={archivedProposals} />
                                </>
                            ) : (
                                <div className="text-center py-8">
                                    <p className="text-gray-500">No archived proposals</p>
                                </div>
                            )}
                        </CollapsibleSection>
                    )}
                </div>

                {/* Footer Links */}
                <div className="mt-8 pt-6 border-t border-gray-200 flex justify-center gap-6 text-sm">
                    <Link
                        to="/jobs"
                        className="text-secondary-600 hover:text-secondary-700 font-medium"
                    >
                        Search for jobs
                    </Link>
                    <span className="text-gray-300">|</span>
                    <Link
                        to="/profile"
                        className="text-secondary-600 hover:text-secondary-700 font-medium"
                    >
                        Manage your profile
                    </Link>
                </div>
            </div>

            {/* Proposal Details Modal */}
            {selectedProposal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden relative flex flex-col">
                        {/* Modal Header with Navigation */}
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10 flex-shrink-0">
                            <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <h2 className="text-lg font-semibold text-gray-900 truncate">
                                        Proposal Details
                                    </h2>
                                </div>
                                
                                {/* Navigation Controls */}
                                {displayProposals.length > 1 && (
                                    <ModalNavigation
                                        currentIndex={selectedProposalIndex}
                                        totalCount={displayProposals.length}
                                        onPrevious={() => handleNavigateProposal(-1)}
                                        onNext={() => handleNavigateProposal(1)}
                                        itemName="Proposal"
                                    />
                                )}
                                
                                <button
                                    onClick={handleCloseModal}
                                    className="text-gray-400 hover:text-gray-600 transition-colors rounded-full p-1 hover:bg-gray-100 flex-shrink-0"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                        
                        {/* Modal Content */}
                        <div className="flex-1 overflow-y-auto">
                            <InlineProposalDetails
                                proposal={selectedProposal}
                                onUpdate={refreshActiveData}
                                onClose={handleCloseModal}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
